import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { Details } from './Details';
import { StyledTableHeaderCell } from 'views/TirePressure/Components/NotificationTable.styles';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import type { SquaresAlert, AlertData } from 'types/squares.type';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const PMsView = ({
  enableDarkTheme,
  notification,
}: {
  enableDarkTheme: boolean;
  notification: SquaresAlert;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const fontColor = palette.textPrimary;

  return (
    <Box>
      <Details enableDarkTheme={enableDarkTheme} notification={notification} />
      <Typography
        sx={{
          fontSize: '12px',
          color: ThemePalette.typography.grey,
          fontWeight: '600',
        }}
      >
        {t('description')}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          color: fontColor,
          marginTop: '10px',
        }}
        dangerouslySetInnerHTML={{ __html: notification.message }}
      />

      <Typography
        sx={{
          fontSize: '12px',
          color: ThemePalette.typography.grey,
          marginTop: '40px',
          fontWeight: '600',
        }}
      >
        {t('repeated_work_orders')}
      </Typography>
      <Box
        sx={{
          marginTop: '20px',
        }}
      >
        {notification?.data?.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                    {t('pm_type')}
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                    {t('last_pm_work_order_date')}
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                    {t('overdue_since')}
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                    {t('engine_hrs_since_last_pm_wo')}
                  </StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notification?.data?.map((row: AlertData, index: number) => {
                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '& td': {
                          border: 'none',
                          borderBottom: '1px solid #2F445E',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {row.pm_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {convertUtcToDisplayTime(
                            row.last_wo_date,
                            preferredTimeDisplayMode,
                            timezone,
                            DATE_TIME_FORMAT
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {convertUtcToDisplayTime(
                            row.last_active_date,
                            preferredTimeDisplayMode,
                            timezone,
                            DATE_TIME_FORMAT
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {row.current_engine_hours}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            sx={{
              fontSize: '14px',
              color: ThemePalette.typography.grey,
            }}
          >
            {t('no_repeated_work_orders')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
